import React, { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { ApiError, api } from '../../lib/api'
import { useAuth } from '../../context/AuthContext'

// Combined invite-acceptance + password-setup screen. There's no useful
// intermediate state between "this invite link is valid" and "set your
// password" — accepting the invite IS setting the password, in one request
// (POST /provider-invites/{token}/accept) — so this is deliberately one
// screen rather than two separate routes.
export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { loginWithToken } = useAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    if (!acceptedTerms) {
      setError('You must agree to the Terms of Service and acknowledge the Privacy Policy to continue.')
      return
    }
    setBusy(true)
    try {
      const result = await api(`/provider-invites/${token}/accept`, {
        method: 'POST',
        body: JSON.stringify({ password, accepted_terms: acceptedTerms }),
      })
      await loginWithToken(result.access_token)
      setDone(true)
      // Never the dashboard — a freshly accepted invite is always
      // onboarding-incomplete at this moment, so onboarding is this
      // account's first authenticated experience, same as a fresh self-signup.
      setTimeout(() => navigate('/practice/provider/onboarding', { replace: true }), 800)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'This invite link is invalid or has expired. Ask your practice admin to resend it.')
      } else {
        setError('Something went wrong. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-shell warm-theme">
      <div className="auth-card">
        <h1>Welcome to ATUUNE</h1>
        <p className="muted" style={{ fontSize: 13.5 }}>You've been invited to join as a provider. Choose a password to get started — you'll complete your profile next.</p>

        {done ? (
          <p style={{ color: 'var(--green)', fontWeight: 700 }}>Account ready — signing you in…</p>
        ) : (
          <form onSubmit={submit}>
            <div className="field">
              <label>New password</label>
              <input className="inp" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoFocus />
            </div>
            <div className="field">
              <label>Confirm password</label>
              <input className="inp" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} />
            </div>
            <div className="field" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 8 }}>
              <input type="checkbox" id="acceptedTerms" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} style={{ marginTop: 3 }} aria-required="true" />
              <label htmlFor="acceptedTerms" style={{ margin: 0, fontSize: 13.5 }}>
                I have read and agree to the <Link to="/terms" target="_blank">Terms of Service</Link> and acknowledge the <Link to="/privacy" target="_blank">Privacy Policy</Link>.
              </label>
            </div>
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="btn full" type="submit" disabled={busy}>{busy ? 'Setting up…' : 'Set password & sign in'}</button>
          </form>
        )}
      </div>
    </div>
  )
}
